import React, { useCallback, useState } from 'react';
import isEqual from 'lodash/isEqual';

import { NavSpyLink, NavScrollSpySection } from '@ad-scripts/presentation/components/common/nav/nav_scroll_spy';
import ActionsFooter from 'src/presentation/components/pages/shared/ActionsFooter';
import ConfirmCancelModal from 'src/presentation/components/forms/ConfirmCancelModal';
import Form from 'src/presentation/components/forms/Form';
import { getCleanedConfig } from './helpers/chatUtils';
import { ScrollToFirstError } from './ChatLayout';
import { MobileChatStyleSection } from './sections/ChatStyleSection';
import { MobileChatTextSection } from './sections/ChatTextSection';
import { MobileChatConfigurationSection } from './sections/ChatConfigurationSection';
import ChatThrottlingSection from './sections/ChatThrottlingSection';
import { StyledBody, StyledNavScrollSpy, StyledNavScrollSpyContainer } from './WebChatEditor';
import { CHAT_BRAND } from 'src/constants/branding';

const MobileChatEditor = ({ actionsRef, errors, ...props }) => {
  const { editorType, initializedItem, formContext, handleNeedHelp } = props;

  const { onSave, onClose, workingState } = formContext;

  let editor: any;
  switch (editorType) {
    case 'throttling':
      editor = <ChatThrottlingSection />;
      break;
    default:
      editor = configurationEditor({ errors });
  }

  const [isConfirmCancelModalVisible, setConfirmCancelModalVisible] = useState(false);

  const handleStay = useCallback(() => {
    setConfirmCancelModalVisible(false);
  }, [setConfirmCancelModalVisible]);

  const confirmCancel = useCallback(() => {
    const modified = !isEqual(getCleanedConfig(workingState), getCleanedConfig(initializedItem));
    modified ? setConfirmCancelModalVisible(true) : onClose();
  }, [initializedItem, onClose, setConfirmCancelModalVisible, workingState]);

  return (
    <>
      <Form context={formContext} preventSaveOnEnter>
        {editor}
        <ActionsFooter onClose={confirmCancel} onSave={onSave} ref={actionsRef} onHelp={handleNeedHelp} />
      </Form>
      <ConfirmCancelModal
        name={CHAT_BRAND.name}
        handleLeave={onClose}
        handleStay={handleStay}
        isModified={isConfirmCancelModalVisible}
        isVisible={isConfirmCancelModalVisible}
      />
    </>
  );
};

function configurationEditor({ errors }) {
  const bodyRef = React.createRef();
  const sectionConfigurationRef = React.createRef();
  const sectionStyleRef = React.createRef();
  const sectionTextRef = React.createRef();

  return (
    <StyledBody data-aid="chat-editor" ref={bodyRef}>
      <StyledNavScrollSpy>
        <NavSpyLink sectionRef={sectionConfigurationRef}>Configuration</NavSpyLink>
        <NavSpyLink sectionRef={sectionStyleRef}>Style</NavSpyLink>
        <NavSpyLink sectionRef={sectionTextRef}>Text</NavSpyLink>
      </StyledNavScrollSpy>
      <StyledNavScrollSpyContainer
        parentDataAid={'admin-chat-configuration-page-modal-editor'}
        sectionActiveClassName={'adminChatEditor-isActive'}
      >
        <ScrollToFirstError errors={errors} />
        <NavScrollSpySection ref={sectionConfigurationRef} data-aid="config-section">
          <MobileChatConfigurationSection />
        </NavScrollSpySection>
        <NavScrollSpySection ref={sectionStyleRef} data-aid="style-section">
          <MobileChatStyleSection />
        </NavScrollSpySection>
        <NavScrollSpySection ref={sectionTextRef} data-aid="text-section">
          <MobileChatTextSection />
        </NavScrollSpySection>
      </StyledNavScrollSpyContainer>
    </StyledBody>
  );
}

export default MobileChatEditor;
